const cities = [
  { name: "Palm Harbor", zips: "34683, 34684, 34685" },
  { name: "Tarpon Springs", zips: "34689, 34688" },
  { name: "Dunedin", zips: "34698" },
  { name: "Clearwater", zips: "33755–33769" },
  { name: "Safety Harbor", zips: "34695" },
  { name: "Oldsmar", zips: "34677" },
  { name: "East Lake", zips: "34677, 34685" },
  { name: "Trinity", zips: "34655" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from home in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle the rest.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function PalmHarborLocalAreaSection() {
  return (
    <section id="service-area" className="bg-[#F7F8FA] pt-6 md:pt-8 pb-20 md:pb-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Palm Harbor & Northern Pinellas County
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Whether you are in Palm Harbor, Tarpon Springs, Dunedin, Clearwater, or anywhere in northern Pinellas County, Fast Passport Center makes expedited passport processing simple — no drive to Miami, no agency lines, no guessing.
          </p>
        </div>

        {/* Full-width editorial intro */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-12">
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Palm Harbor sits along the northern Gulf Coast of Pinellas County, a thriving suburban community of roughly 60,000 residents that offers something increasingly rare in Florida: the feeling of a genuine hometown with immediate access to world-class beaches, nature preserves, and the amenities of the Tampa Bay metro. Unlike the dense urban corridors of southern Pinellas or the heavy tourism of Clearwater Beach, Palm Harbor maintains a quieter, residential character — tree-lined neighborhoods, golf course communities, and waterfront homes along Lake Tarpon and the Intracoastal Waterway.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            What defines Palm Harbor is its position as a gateway. To the west, Honeymoon Island State Park and Caladesi Island — consistently ranked among the best beaches in the United States — are just minutes away. To the south, the Pinellas Trail runs through neighboring Dunedin, offering 47 miles of paved cycling and walking paths connecting Gulf Coast communities. To the east, the historic sponge docks of Tarpon Springs bring Greek heritage, fresh seafood, and a cultural identity unlike anything else in Florida. And to the south, downtown St. Petersburg and Tampa International Airport are within easy reach via US-19, the Courtney Campbell Causeway, or the Veterans Expressway.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Palm Harbor's demographic profile reflects a blend of established families, retirees who relocated from northern states, and young professionals commuting into Tampa or St. Petersburg. The community is home to top-rated schools, the Innisbrook Resort and Golf Club (which hosts the PGA Tour's Valspar Championship each spring), and a growing number of local restaurants and breweries along Florida Avenue and US-19. Residents here value quality of life — good schools, safe neighborhoods, outdoor recreation, and proximity to both Gulf beaches and urban employment centers.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The travel patterns of Palm Harbor residents reflect this diverse community. Families with school-age children travel to the Caribbean and Europe during summer breaks and spring vacation weeks. Retirees who have settled in Palm Harbor's golf communities and waterfront condominiums cruise regularly — Port Tampa Bay, Port Canaveral, and Port Everglades are all accessible for Caribbean itineraries. Young professionals fly from Tampa International Airport for business trips to domestic hubs and international destinations. And seasonal residents who split their time between Palm Harbor and northern homes often discover passport issues during their seasonal transition, when they shift from domestic routines to international travel planning.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The geographic reality matters for passport timing. Palm Harbor is approximately 25 miles northwest of Tampa International Airport (TPA) via the Veterans Expressway or US-19 — a 35–50 minute drive depending on traffic. St. Pete-Clearwater International Airport (PIE) is closer at roughly 12 miles south, but offers fewer international flight options. The closest regional passport agency is in Miami, approximately 280 miles south — a drive that is not practical for Palm Harbor residents with urgent travel needs. This distance makes courier-based expedited passport services not just convenient, but essential for travelers with tight timelines.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            Fast Passport Center has processed expedited passports for Palm Harbor travelers across every demographic — families preparing for Caribbean cruises, retirees departing on European river cruises, business travelers with sudden international meetings, and seasonal residents discovering expired passports during their Florida stay. Our courier-based service means you start online from your Palm Harbor home, ship your documents overnight from any UPS or FedEx location along US-19 or Curlew Road, and your passport arrives within days — no drive to Miami, no waiting in agency lines, no uncertainty.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Office + How It Works */}
          <div>
            {/* FPC Office Card */}
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-building-2-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Tampa</p>
                  <p className="text-white/60 text-xs mt-0.5">400 N Ashley Dr #1700, Tampa, FL 33602</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Nearest office to Palm Harbor — approximately 25 miles southeast via US-19 or Veterans Expressway
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Ship overnight from any UPS/FedEx along US-19 or Curlew Road</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">courier pickup</strong> from your Palm Harbor home in most cases, <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location, or <strong className="text-white">schedule a drop-off</strong> at our nearest office. Most clients ship overnight — no visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* How FPC Helps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-white rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cities We Serve + Trust strip */}
          <div>
            <div className="bg-white rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Cities & Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-[#F7F8FA] rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in northern Pinellas County and western Pasco — start online, ship overnight, and we handle the rest. No need to drive to Miami.
              </p>
            </div>

            {/* Internal links */}
            <div className="bg-white rounded-lg p-5 mb-6 border border-gray-200">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">Nearby Cities We Serve</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: "Clearwater", href: "/clearwater" },
                  { name: "Dunedin", href: "/dunedin" },
                  { name: "St. Petersburg", href: "/st-petersburg" },
                  { name: "Tampa", href: "/tampa" },
                  { name: "Sarasota", href: "/sarasota" },
                  { name: "Venice", href: "/venice" },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                  >
                    {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Trust credentials strip */}
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}